import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Link
} from "react-router-dom";
import {
  Search,
  CreditCard,
  ExternalLink,
  FileText,
  UserCheck
} from "lucide-react";
import {
  expandLedgerList
} from "../../utils/feeLedgerDisplay";
import {
  getStudentFeeLedger,
  getStudentFeeLedgerPortal
} from "../../api/fees/feeApi";
import {
  getAllStudents,
  getAllSections,
  getAllClasses
} from "../../api/student/studentApi";
import {
  getAllStudentsForPortal,
  getAllSectionsForPortal,
  getAllClassesForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal,
  getActiveAcademicYear,
  getActiveAcademicYearPortal
} from "../../api/academicyear/academicYearApi";
import CollectFeeModal from "../../components/fees/CollectFeeModal";
import FeeReceiptModal from "../../components/fees/FeeReceiptModal";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const studentDisplayName = (s) =>
  [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ") || "—";

const statusBadgeVariant = {
  PAID: "success",
  PARTIAL: "warning",
  UNPAID: "danger",
  OVERDUE: "danger",
};

const FeeCollectPage = ({ isPortal = false, canCollect = true }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterClassId, setFilterClassId] = useState("");
  const [filterSectionId, setFilterSectionId] = useState("");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [academicYearId, setAcademicYearId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [results, setResults] = useState([]);
  const [student, setStudent] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const toast = useToast();

  // Load Classes & Academic Years on Mount
  useEffect(() => {
    (isPortal ? getAllClassesForPortal() : getAllClasses())
      .then((d) => setClasses(d || []))
      .catch(() => {});

    (async () => {
      try {
        const ayRes = isPortal
          ? await getAllAcademicYearsPortal()
          : await getAllAcademicYears();
        const years = ayRes?.data || ayRes || [];
        setAcademicYears(Array.isArray(years) ? years : []);
        try {
          const activeRes = isPortal
            ? await getActiveAcademicYearPortal()
            : await getActiveAcademicYear();
          const active = activeRes?.data || activeRes;
          if (active?._id) setAcademicYearId(active._id);
          else {
            const found = (Array.isArray(years) ? years : []).find((y) => y.isActive);
            if (found) setAcademicYearId(found._id);
          }
        } catch {
          const found = (Array.isArray(years) ? years : []).find((y) => y.isActive);
          if (found) setAcademicYearId(found._id);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [isPortal]);

  // Load Sections dynamically based on selected Class
  useEffect(() => {
    setFilterSectionId("");
    if (!filterClassId) {
      setSections([]);
      return;
    }
    setSectionsLoading(true);
    (isPortal ? getAllSectionsForPortal(filterClassId) : getAllSections(filterClassId))
      .then((d) => {
        const list = d?.data || d || [];
        setSections(Array.isArray(list) ? list : []);
      })
      .catch(() => setSections([]))
      .finally(() => setSectionsLoading(false));
  }, [filterClassId, isPortal]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStudent(null);
    setLedger([]);
    setSummary(null);
    setResults([]);
    try {
      const filters = { status: "ENROLLED" };
      if (filterClassId) filters.classId = filterClassId;
      if (filterSectionId) filters.sectionId = filterSectionId;
      const data = isPortal
        ? await getAllStudentsForPortal(filters)
        : await getAllStudents(filters);
      let list = Array.isArray(data) ? data : data?.data || [];
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((s) => {
          const name = studentDisplayName(s).toLowerCase();
          return (
            name.includes(q) ||
            (s.admissionNo || "").toLowerCase().includes(q) ||
            (s.rollNumber || "").toLowerCase().includes(q) ||
            (s.studentId || "").toLowerCase().includes(q)
          );
        });
      }
      setResults(list.slice(0, 50));
      if (list.length === 0) toast.error("No students found.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (stu) => {
    setStudent(stu);
    setResults([]);
    setLoadingLedger(true);
    try {
      const res = isPortal
        ? await getStudentFeeLedgerPortal(stu._id)
        : await getStudentFeeLedger(stu._id);
      const payload = res.data || res || {};
      setLedger(payload.ledger || []);
      setSummary(payload.summary || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load ledger.");
      setLedger([]);
      setSummary(null);
    } finally {
      setLoadingLedger(false);
    }
  };

  const onCollectSuccess = (receipt) => {
    toast.success("Payment collected successfully.");
    setReceiptData(receipt);
    setReceiptOpen(true);
    if (student) selectStudent(student);
  };

  // Option Derivations for Form Dropdowns
  const academicYearOptions = useMemo(
    () => [
      { label: "Select Academic Year", value: "" },
      ...academicYears.map((y) => ({
        label: `${y.yearName}${y.isActive ? " (Active)" : ""}`,
        value: y._id,
      })),
    ],
    [academicYears]
  );

  const classOptions = useMemo(
    () => [
      { label: "All Classes", value: "" },
      ...classes.map((c) => ({ label: `Class ${c.className}`, value: c._id })),
    ],
    [classes]
  );

  const sectionOptions = useMemo(
    () => [
      { label: sectionsLoading ? "Loading…" : "All Sections", value: "" },
      ...sections.map((s) => ({
        label: `Section ${s.sectionName || s.name}`,
        value: s._id,
      })),
    ],
    [sections, sectionsLoading]
  );

  // Table rows derivation
  const tableRows = useMemo(() => {
    return expandLedgerList(ledger).map((r) => ({
      ...r,
      id: r.key,
      netBase: Math.max(0, r.netAmount - r.fineAmount),
    }));
  }, [ledger]);

  const columns = [
    {
      key: "billTitle",
      header: "Title",
      render: (r) => (
        <div>
          {r.isFirst ? (
            <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.billTitle}</span>
          ) : null}
          {r.isFirst && r.headCount > 1 && (
            <span className="ml-1.5 text-xs text-neutral-400 dark:text-neutral-500">({r.headCount})</span>
          )}
        </div>
      ),
    },
    {
      key: "headTitle",
      header: "Fee Head",
      render: (r) => <span className="font-medium text-neutral-800 dark:text-neutral-100">{r.headTitle}</span>,
    },
    {
      key: "netBase",
      header: "Net Fee",
      align: "right",
      render: (r) => `₹${r.netBase.toFixed(2)}`,
    },
    {
      key: "fineAmount",
      header: "Late Fine",
      align: "right",
      render: (r) => (
        <span className={r.fineAmount > 0 ? "font-medium text-warning-700" : ""}>
          ₹{r.fineAmount.toFixed(2)}
        </span>
      ),
    },
    {
      key: "paidAmount",
      header: "Paid",
      align: "right",
      render: (r) => (
        <span className="text-success-700">₹{r.paidAmount.toFixed(2)}</span>
      ),
    },
    {
      key: "dueAmount",
      header: "Due",
      align: "right",
      render: (r) => (
        <span className={r.dueAmount > 0 ? "font-semibold text-danger-500" : ""}>
          ₹{r.dueAmount.toFixed(2)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={statusBadgeVariant[r.status] || "default"}>
          {r.status}
        </Badge>
      ),
    },
  ];

  const ledgerPath = isPortal
    ? `/staff-panel/fees/ledger/${student?._id}`
    : `/fees/ledger/${student?._id}`;

  const dueReportPath = isPortal
    ? "/staff-panel/fees/due-report"
    : "/fees/due-report";

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Collect Fee</h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            Search for a student and record payments via standard (FIFO) ordering.
          </p>
        </div>
        <Link
          to={dueReportPath}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-900 dark:text-primary-300 hover:underline"
        >
          <FileText className="h-4 w-4" />
          Defaulters report
        </Link>
      </div>

      {/* Alerts */}
      
      

      {/* Search and Filters Card */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="w-full sm:w-64">
            <Select
              label="Academic Year (for payment)"
              options={academicYearOptions}
              value={academicYearId}
              onChange={(val) => setAcademicYearId(val)}
            />
          </div>

          <form onSubmit={handleSearch} className="grid gap-3 sm:grid-cols-4 items-end">
            <div className="sm:col-span-1">
              <Input
                placeholder="Name / Adm No / Roll"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <div>
              <Select
                options={classOptions}
                value={filterClassId}
                onChange={(val) => setFilterClassId(val)}
              />
            </div>
            <div>
              <Select
                options={sectionOptions}
                value={filterSectionId}
                onChange={(val) => setFilterSectionId(val)}
                disabled={!filterClassId || sectionsLoading}
              />
            </div>
            <div>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={loading}
                leftIcon={<Search className="h-4 w-4" />}
              >
                Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Search Results Dropdown/List */}
      {results.length > 0 && (
        <Card>
          <CardHeader className="bg-neutral-50 dark:bg-neutral-950 py-2.5">
            <CardTitle className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Select Student ({results.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-60 overflow-y-auto divide-y">
            {results.map((s) => (
              <button
                key={s._id}
                type="button"
                onClick={() => selectStudent(s)}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {studentDisplayName(s)}
                  </p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                    Adm: {s.admissionNo || "—"} · Roll: {s.rollNumber || "—"}
                  </p>
                </div>
                <UserCheck className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Selected Student Ledger and Summary */}
      {student && (
        <Card>
          <CardHeader className="flex flex-col gap-3 border-b bg-neutral-50/50 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {studentDisplayName(student)}
              </CardTitle>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                Admission No: <span className="font-medium text-neutral-700 dark:text-neutral-200">{student.admissionNo}</span> · Roll No: <span className="font-medium text-neutral-700 dark:text-neutral-200">{student.rollNumber || "—"}</span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Link to={ledgerPath}>
                <Button variant="outline-primary" size="sm" rightIcon={<ExternalLink className="h-3.5 w-3.5" />}>
                  Full Ledger
                </Button>
              </Link>
              {canCollect && Number(summary?.totalOutstanding || 0) > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!academicYearId}
                  onClick={() => setCollectOpen(true)}
                  leftIcon={<CreditCard className="h-4 w-4" />}
                >
                  Collect Payment
                </Button>
              )}
            </div>
          </CardHeader>

          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b bg-neutral-50/50 dark:bg-neutral-900/50">
              <div className="rounded-md border bg-white dark:bg-neutral-900 p-3 shadow-xs">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Total Net Fee</p>
                <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                  ₹{Number(summary.totalNet || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-md border bg-white dark:bg-neutral-900 p-3 shadow-xs">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Late Fine</p>
                <p className="text-lg font-semibold text-warning-700 mt-1">
                  ₹{Number(summary.totalFine || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-md border bg-white dark:bg-neutral-900 p-3 shadow-xs">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Total Paid</p>
                <p className="text-lg font-semibold text-success-700 mt-1">
                  ₹{Number(summary.totalPaid || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-md border bg-white dark:bg-neutral-900 p-3 shadow-xs">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Outstanding</p>
                <p className="text-lg font-semibold text-danger-500 mt-1">
                  ₹{Number(summary.totalOutstanding || 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          <CardContent className="p-0">
            {loadingLedger ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-4">
                    <Skeleton height={16} width="20%" />
                    <Skeleton height={16} width="25%" />
                    <Skeleton height={16} width="15%" />
                    <Skeleton height={16} width="20%" />
                  </div>
                ))}
              </div>
            ) : (
              <Table
                columns={columns}
                data={tableRows}
                emptyState={
                  <EmptyState
                    icon={FileText}
                    title="No published ledger items"
                    description="There are no fee records calculated for this student."
                  />
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Collect Fee Action Modal */}
      <CollectFeeModal
        isOpen={collectOpen}
        onClose={() => setCollectOpen(false)}
        onSuccess={onCollectSuccess}
        student={student}
        summary={summary}
        academicYearId={academicYearId}
        isPortal={isPortal}
      />

      {/* Fee Receipt Display Modal */}
      <FeeReceiptModal
        isOpen={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receipt={receiptData}
      />
    </div>
  );
};

export default FeeCollectPage;